// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { Task, WithOptional, WithRequiredNotNull, WorkCellTask } from '@evolvemep/api-ts';

import { GridMenu } from 'components/Mantine/GridMenu';
import type { PageTitle } from 'components/Mantine/Navigation/types';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { canUncompleteTask } from 'modules/Shop/Tasks/common';
import { ManageTaskTimeModal } from 'modules/Shop/Tasks/ManageTaskTimeModal/ManageTaskTimeModal';
import { CompleteFinalTaskModal } from 'modules/Shop/Tasks/TaskViewer/CompleteFinalTaskModal';
import { EditTaskAssigneesPopover } from 'modules/Shop/Tasks/TaskViewer/EditTaskAssigneesPopover';
import { UncompleteTaskModal } from 'modules/Shop/Tasks/TaskViewer/UncompleteTaskModal';
import { useCompleteTask } from 'modules/Shop/Tasks/TaskViewer/useCompleteTask';
import { SetTaskWorkCellPopover } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/SetTaskWorkCellPopover';
import type { WorkCellSelectProps } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/WorkCellSelect';

type Props = {
  task: WithOptional<
    Pick<Task, 'taskId' | 'taskName' | 'taskStatusTypeName' | 'dateStarted' | 'assignees'>,
    'assignees'
  >;
  workCellTask?: WorkCellTask;
  onTaskUpdated: (updatedTask: Task) => void;
  parentPage: PageTitle | string;
} & Pick<WorkCellSelectProps, 'facility'>;

export const ActionTaskMenu = ({ task, workCellTask, onTaskUpdated, parentPage, facility }: Props) => {
  const [showWillCompleteWOWarning, setShowWillCompleteWOWarning] = useState(false);
  const { taskId, taskStatusTypeName } = task;
  const canUncomplete = canUncompleteTask(task);
  const navigate = useEvolveNavigate(parentPage);
  const { attemptComplete, completeTask, loading } = useCompleteTask(task, onTaskUpdated);

  return (
    <GridMenu
      options={[
        {
          label: taskStatusTypeName === 'Not Started' ? 'Start' : 'Manage time',
          value: 'manage-time',
          // a completed task that never ran has no time to manage, and resuming it from here would contradict
          // the uncomplete action below
          hidden: canUncomplete,
          icon: taskStatusTypeName === 'Not Started' ? 'Play' : 'TimeTracking',
          iconSize: 'sm',
        },
        {
          label: 'Complete',
          value: 'complete',
          hidden: taskStatusTypeName === 'Completed',
          icon: 'Check',
        },
        {
          label: 'Uncomplete',
          value: 'uncomplete',
          hidden: !canUncomplete,
          icon: 'Reset',
        },
        {
          value: 'edit-assignees',
          label: `${task.assignees?.length === 0 ? 'Add' : 'Edit'} assignees`,
          hidden: isNil(task.assignees),
        },
        {
          label: 'Assign work cell',
          value: 'set-work-cell',
          hidden: taskStatusTypeName === 'Completed',
        },
        {
          value: 'View details',
        },
      ]}
      loading={loading}
      onMenuItemSelected={async (menuItemSelected) => {
        if (menuItemSelected === 'View details') {
          void navigate(`/task-viewer/${taskId}`);
          return true;
        }

        if (menuItemSelected === 'complete') {
          try {
            const didComplete = await attemptComplete();
            // We can't just use `menuItemSelected` and the return value here since this is async.
            // If we did, the modal would show up for a moment while `attemptComplete` was checking work order status.
            // So we update `showWillCompleteWOWarning` if the work order wasn't completed by `attemptComplete`.
            if (!didComplete) {
              setShowWillCompleteWOWarning(true);
            }
            return didComplete;
          } catch {
            return true;
          }
        }

        return false;
      }}
    >
      {(menuItemSelected, resetMenu) => (
        <>
          <SetTaskWorkCellPopover
            opened={menuItemSelected === 'set-work-cell'}
            task={task}
            onClose={resetMenu}
            onTaskUpdated={onTaskUpdated}
            facility={facility}
          />
          <ManageTaskTimeModal
            taskId={taskId}
            workCellTask={workCellTask}
            onTaskUpdated={onTaskUpdated}
            opened={menuItemSelected === 'manage-time'}
            onClose={resetMenu}
          />
          <UncompleteTaskModal
            task={task}
            workCellTask={workCellTask}
            opened={menuItemSelected === 'uncomplete'}
            onClose={resetMenu}
            onTaskUncompleted={onTaskUpdated}
          />
          <CompleteFinalTaskModal
            workCellTask={workCellTask}
            opened={showWillCompleteWOWarning}
            onClose={() => {
              setShowWillCompleteWOWarning(false);
              resetMenu();
            }}
            onConfirm={completeTask}
          />
          {isNotNil(task.assignees) && (
            <EditTaskAssigneesPopover
              opened={menuItemSelected === 'edit-assignees'}
              onClose={resetMenu}
              onTaskUpdated={onTaskUpdated}
              // Not sure why TS isn't properly type-narrowing here
              task={task as WithRequiredNotNull<typeof task, 'assignees'>}
            />
          )}
        </>
      )}
    </GridMenu>
  );
};
